import { t } from '@app/react/utils/i18n';
import { ErrorInterpretation } from '@healthinal/react-error-utils';
import { AxiosError, HttpStatusCode, isAxiosError } from 'axios';
import { useCallback } from 'react';

declare module '@healthinal/react-error-utils' {
  interface ErrorInterpretation {
    title: string;
  }
}

const serverUnavailableStatusCodes = new Set([
  HttpStatusCode.BadGateway,
  HttpStatusCode.ServiceUnavailable,
  HttpStatusCode.GatewayTimeout,
]);

export function interpretError(error: unknown): ErrorInterpretation {
  if (isAxiosError(error)) {
    if (error.code === AxiosError.ERR_NETWORK) {
      return {
        title: t('ErrorHandler.BackendOfflineErrorTitle'),
        message: t('ErrorHandler.BackendOfflineErrorText'),
        shouldBeReported: false,
      };
    }

    if (error.response?.status && serverUnavailableStatusCodes.has(error.response.status)) {
      return {
        title: t('ErrorHandler.BackendOfflineErrorTitle'),
        message: t('ErrorHandler.BackendOfflineErrorText'),
        shouldBeReported: false,
      };
    }

    // Reaches us only once the token refresh has already failed, so the session is really gone.
    if (error.response?.status === HttpStatusCode.Unauthorized) {
      return {
        title: t('ErrorHandler.SessionExpiredTitle'),
        message: t('ErrorHandler.SessionExpiredText'),
        shouldBeReported: false,
      };
    }

    // Same keys as Angular's forbidden dialog, so both stacks show the same text.
    if (error.response?.status === HttpStatusCode.Forbidden) {
      return {
        title: t('ErrorHandler.BadRequestTitle'),
        message: t('ErrorHandler.ForbiddenText'),
        shouldBeReported: false,
      };
    }
  }

  return {
    title: t('ErrorHandler.InternalServerErrorProdTitle'),
    message: t('ErrorHandler.InternalServerErrorProdText'),
    shouldBeReported: true,
  };
}

export function useInterpretError() {
  return useCallback(interpretError, []);
}
